import { Component } from "@/types/types"
import { cx } from "@/utils/cx"
import { ChangeEvent, HTMLInputAutoCompleteAttribute, useEffect, useRef, useState } from "react"
import { Stack } from "@/ui/atoms/layout/Stack"
import { tx } from "@/utils/tx"
import { Icon } from "../../media/Icon"
import { Tooltip } from "../../controls/Tooltip"

export const COMPONENT_NAME = "passwordField"

export const presets = {}

export interface Props extends Component {
  name: string
  label: string
  placeholder?: string
  defaultValue?: string
  helperText?: string
  error?: string
  disabled?: boolean
  autoComplete?: HTMLInputAutoCompleteAttribute
  passwordVisible?: boolean
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  onVisibilityChange?: (isVisible: boolean) => void
}

export function PasswordField({
  name,
  label,
  placeholder = "",
  defaultValue = "",
  helperText = "",
  error = "",
  disabled,
  autoComplete,
  passwordVisible: passedPasswordVisible = false,
  onChange,
  onVisibilityChange,
  className,
  testId = COMPONENT_NAME,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)

  const [passwordVisible, setPasswordVisible] = useState(passedPasswordVisible)

  useEffect(() => {
    setPasswordVisible(passedPasswordVisible)
  }, [passedPasswordVisible])

  useEffect(() => {
    typeof onVisibilityChange === "function" && onVisibilityChange(passwordVisible)
  }, [passwordVisible])

  function handleVisibilityButtonClick() {
    setPasswordVisible(!passwordVisible)
  }

  return (
    <Stack className={cx(`gap-2 w-full`, className)} testId={testId}>
      <div className="flex justify-between">
        <label
          htmlFor={name}
          className={cx(
            "leading-none text-text-secondary font-semibold",
            disabled && "text-text-secondary-disabled",
          )}
          data-test={tx(testId, "label")}
        >
          {label}
        </label>

        {helperText && (
          <div
            className={cx("text-xs text-text-subtle", disabled && "text-text-subtle-disabled")}
            data-test={tx(testId, "helpText")}
          >
            {helperText}
          </div>
        )}
      </div>
      <div className="relative">
        <div
          className={cx(
            `flex items-center gap-1 bg-surface text-text border border-border-form rounded leading-none`,
            "pr-8",
            `h-[38px] focus-ring`,
            "placeholder:text-text-subtle",
            "disabled:bg-surface-disabled",
            className,
          )}
        >
          <input
            name={name}
            id={name}
            type={passwordVisible ? "text" : "password"}
            defaultValue={defaultValue}
            placeholder={placeholder}
            autoComplete={autoComplete}
            ref={inputRef}
            disabled={disabled}
            onChange={onChange}
            className={cx(`p-2 grow bg-surface focus:outline-none`)}
            data-test={tx(testId, "input")}
          />
        </div>
        <Tooltip side="right" label={passwordVisible ? "Hide password" : "Show password"}>
          <button
            className="size-8 grid place-items-center focus-ring absolute inset-[3px] left-auto rounded"
            type="button"
            onClick={handleVisibilityButtonClick}
          >
            <Icon name={passwordVisible ? "visibilityOffSm" : "visibilitySm"} />
          </button>
        </Tooltip>
      </div>
      {!!error && (
        <div className="text-xs text-text-critical leading-tight" data-test={tx(testId, "error")}>
          {error}
        </div>
      )}
    </Stack>
  )
}
